import React, { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import className from "classnames";
import { Classes } from '@blueprintjs/core';
import { IEFormsAddressPrefill } from "csd.phoenix.models/EFormsAddressPrefill";
import { IFieldOption, IProviderSelectionProps } from "../../../models";
import SelectField from "../../SelectField";
import { formatPhoneNumber } from "../../../../../../../src/utils/dataFormatters";
import { IPrefillData } from '../../../../../../stores/formsStore';
import { isLocaleUK, isLocaleUS } from "../../../../../../i18n"


const ProviderSelectionField: React.FC<IProviderSelectionProps> = (props) => {

  const { values: { Providers, Locations } } = useFormikContext<IPrefillData>();
  const FilteredProviders = useMemo(() => Providers!.filter(p => p.IsNhsProvider || !(props.nhsProvidersOnly)), [Providers, props.nhsProvidersOnly]);
  const options: IFieldOption[] = useMemo(() => FilteredProviders!.map(p => ({ value: p.RecordId! })), [FilteredProviders]);
  const minimalSelectedItem: boolean = !!props.minimalSelectedItem;

  const providers = useMemo(() => FilteredProviders!.map(p => {
    const defaultLocation = Locations!.find(l => l.LocationId === p.DefaultLocationId);

    return {
      ...p,
      address: defaultLocation!.PhysicalAddress,
      phone: defaultLocation!.MainPhone?.Number,
      nhsLocationNumber: defaultLocation?.NHSLocationNumber
    };
  }), [Locations, FilteredProviders]);

  const formatAddress = useCallback((address: IEFormsAddressPrefill) => {
    const stateOrCountyPart = isLocaleUS() ? address.StateProvinceCode : address.AddressLine3! || "";
    const cityPartPopulated = address.City.toUpperCase().indexOf("(MISSING)") === -1; //pipelines defaults to '(missing)' for city
    const addressCityPart = cityPartPopulated ? address.City + ", " : "";
    return (
      <div>
        <div>{address.AddressLine1}</div>
        {address.AddressLine2! && <div>{address.AddressLine2!}</div>}
        <div>{addressCityPart}{stateOrCountyPart}{stateOrCountyPart.length > 0 && "  "}{address.PostalZipCode}</div>
      </div>
    )
  }, []);

  const formatNhsLocationNumber = useCallback((nhsLocationNumber: string | undefined) => {
    if (!!nhsLocationNumber) {
      return "NHS Location Id: " + nhsLocationNumber
    }
    else
      return "(No NHS Location Number)";
  }, []);


  const renderItem = useCallback(({ value }: IFieldOption, isSelectedItem: boolean, isActive = false) => {
    const provider = providers.find(p => p.RecordId === value);

    return (
      <div>
        <div>
          {provider!.FullName}
        </div>
        {!(isSelectedItem && minimalSelectedItem) &&
          <div className={className(Classes.TEXT_SMALL, !isActive && Classes.TEXT_MUTED)}>
            {(isLocaleUS()) && ((provider!.phone! && formatPhoneNumber(provider!.phone!)) || "(No Phone Number)")}
            {isLocaleUK() && formatNhsLocationNumber(provider?.nhsLocationNumber)}
            {(provider!.address! && formatAddress(provider!.address!)) || "(No Address)"}
          </div>
        }
      </div>
    )
  }, [formatAddress, minimalSelectedItem, providers, formatNhsLocationNumber]);
  return (
    <SelectField {...props} options={options} renderItem={renderItem} />
  );
}

export default ProviderSelectionField;
